import { useMemo } from "react";
import { LinkButton } from "../../common/components/Link";
import type ComponentTypes from "../componentTypes";
import classNames from "classnames";
import { resolveUrl } from "../../utils/url/urlResolver";
import { trackBannerClick } from "../../feature/Analytics/ga/contentStream";
import { Icon } from "../../common/components/icon/Icon";
import { IconType } from "../../common/components/icon/IconType";
import { cleanupText } from "./CMSSection";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";

export interface CMSBannerProps {
  type: ComponentTypes.CMSBanner;
  headlineTag?: string;
  headline?: string;
  subheadline?: string;
  linkUrl?: string;
  linkLabel?: string;
  imageUrl?: string;
  mobileImageUrl?: string;
  className?: string;
  target?: "newWindow" | "sameWindow";
}

export const CMSBanner = ({
  headline,
  headlineTag,
  subheadline,
  imageUrl,
  mobileImageUrl,
  linkLabel,
  linkUrl,
  className,
  target,
}: CMSBannerProps) => {
  const screenSize = useNumericScreenSize();
  const isDesktopMode = screenSize > useNumericScreenSize.phone;

  headline = cleanupText(headline);
  subheadline = cleanupText(subheadline);
  linkLabel = cleanupText(linkLabel);

  const showContentBox = !!(headline || subheadline || linkLabel);
  const Headline = (headlineTag?.trim() ?? "p") as keyof JSX.IntrinsicElements;

  const getAspectRatio = () => {
    if (!isDesktopMode) {
      return showContentBox ? "auto" : "16 / 9";
    }
    return "3.35 / 1";
  };

  const bgImageStyle = useMemo(() => {
    const bgImageUrl = pickImage([mobileImageUrl, imageUrl], isDesktopMode);
    if (bgImageUrl) {
      return {
        backgroundImage: `url(${bgImageUrl})`,
        aspectRatio: getAspectRatio(),
      };
    }
    return undefined;
  }, [imageUrl, mobileImageUrl, isDesktopMode]);

  const content = (
    <>
      {bgImageStyle && (
        <div className="scha__banner__background-container">
          <div className={"scha__banner__background"} style={bgImageStyle} />
        </div>
      )}
      {showContentBox && (
        <div className={"scha__banner__content"}>
          {headline && <Headline className={"scha__banner__headline"}>{headline}</Headline>}
          {subheadline && <p className={"scha__banner__headline scha__banner__subheadline"}>{subheadline}</p>}
          {linkLabel && (
            <LinkButton
              iconBefore={<Icon className={"scha__link--arrow-link"} type={IconType.ChevronRight} size={20} />}
              className={"scha__banner__link"}
            >
              {linkLabel}
            </LinkButton>
          )}
        </div>
      )}
    </>
  );

  const cls = classNames("scha__banner", className);

  if (linkUrl) {
    return (
      <a
        href={resolveUrl(linkUrl)}
        className={cls}
        target={target === "newWindow" ? "_blank" : undefined}
        onClick={() => trackBannerClick(linkUrl)}
      >
        {content}
      </a>
    );
  } else {
    return <div className={cls}>{content}</div>;
  }
};

const pickImage = (images: (string | undefined)[], isDesktop: boolean) => {
  let image: string | undefined;
  const index = isDesktop ? 1 : 0;
  image = images[index];
  if (!image) {
    image = images.filter((url) => !!url)[0];
  }
  return image;
};
